import type { PortalDashboard, PublicClientPortal } from "./clientPortalApi";

export const portalSectionTitleKeys = {
  overview: "portal.navigation.servicesShort",
  employees: "portal.navigation.employees",
  facilities: "portal.navigation.facilities",
  coach: "portal.title.coach",
  bookings: "portal.title.bookings",
  reservation: "portal.title.reservation",
  "event-registration": "events.registration",
  subscriptions: "portal.title.subscriptions",
  passes: "passes.title",
  reviews: "portal.title.reviews",
  access: "portal.title.access",
} as const;

export type PortalSection = keyof typeof portalSectionTitleKeys;

export function portalSection(value: string | null): PortalSection {
  if (value === "booking") return "bookings";
  return value && Object.hasOwn(portalSectionTitleKeys, value) ? value as PortalSection : "overview";
}

export function portalSectionEnabled(section: PortalSection, portal: PublicClientPortal): boolean {
  if (section === "overview" || section === "access") return true;
  if (section === "bookings" || section === "event-registration") return portal.applicationCodes.some((code) => code === "booking" || code === "tickets");
  if (section === "employees" || section === "facilities") return portal.applicationCodes.includes("booking") && portal.navigation?.[section] !== false;
  if (section === "reservation") return portal.applicationCodes.includes("booking");
  return portal.applicationCodes.includes(section);
}

export function portalSectionAccessible(section: PortalSection, portal: PublicClientPortal, dashboard: PortalDashboard): boolean {
  if (!portalSectionEnabled(section, portal)) return false;
  if (section === "overview" || section === "access") return true;
  const allowed = dashboard.allowedApplicationCodes ?? (dashboard.linked ? portal.applicationCodes : []);
  const member = dashboard.linked && (!dashboard.session || dashboard.session.membershipStatus === "active");
  if (section === "bookings") return member && allowed.some((code) => code === "booking" || code === "tickets");
  if (section === "coach" || section === "passes") return member && allowed.includes(section);
  if (section === "event-registration") return allowed.some((code) => code === "booking" || code === "tickets");
  if (section === "employees" || section === "facilities" || section === "reservation") return allowed.includes("booking");
  return allowed.includes(section);
}

export function legacyPortalSection(hash: string): PortalSection | null {
  if (hash === "#employees") return "employees";
  if (hash === "#facilities") return "facilities";
  if (hash === "#upcoming-bookings") return "bookings";
  return null;
}
